import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { affiliateIndustries } from "./HomeSharedElements";
import NextLink from "next/link";
import { getUrls } from "@portal/app/_common/lib/urls";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import { paletteOptions } from "@portal/app/_components/theme/options/palette";
import { HomepageHeaderTypography } from "@portal/app/HomePage";
import { IndustryVocation } from "@portal/app/page";
import industryHealthcare from "@portal/app/_components/resources/images/homepage_industry_healthcare.webp";
import industryTech from "@portal/app/_components/resources/images/homepage_industry_tech.webp";
import industryBusiness from "@portal/app/_components/resources/images/homepage_industry_business.webp";
import industryTrades from "@portal/app/_components/resources/images/homepage_industry_trades.webp";

const industryImages: Partial<Record<GQL.VocationIndustry, string>> = {
  HEALTH: industryHealthcare.src,
  TECHNICAL_AND_COMMUNICATIONS: industryTech.src,
  BUSINESS_AND_OFFICE: industryBusiness.src,
  TRADE_AND_INDUSTRY: industryTrades.src,
};

const MAX_CHIPS = 4;

export const HomeIndustrySearchSection = ({
  zip,
  shouldPromptForLocation = false,
  industryVocations,
}: {
  zip: string;
  shouldPromptForLocation?: boolean;
  industryVocations?: Partial<Record<GQL.VocationIndustry, IndustryVocation[]>>;
}) => {
  const urls = getUrls();
  const analytics = useMixpanel();

  return (
    <Section>
      <HomepageHeaderTypography className="title">
        Explore by industry
      </HomepageHeaderTypography>
      <IndustryCards>
        {affiliateIndustries.map(({ label, numCareers, industry }) => (
          <CardWrapper key={industry}>
            <IndustryCard>
              <NextLink
                href={urls.DreamSearch({ industry, zip, shouldPromptForLocation })}
                onClick={() =>
                  analytics.track("Homepage - Navigate to DreamSearch", {
                    industry,
                    zip,
                  })
                }
                className="card_main_link"
              >
                <div className="card_image_wrapper">
                  <div className="card_image_bg">
                    <div className="card_image_gradient" />
                  </div>
                  <img
                    alt={`${label} professional`}
                    src={industryImages[industry]}
                    loading="lazy"
                    className="card_portrait"
                  />
                </div>
                <div className="card_info">
                  <Typography className="card_label">{label}</Typography>
                  <Typography className="card_count">
                    {numCareers}+ programs
                  </Typography>
                </div>
              </NextLink>
              <div className="card_chips">
                {industryVocations?.[industry]?.slice(0, MAX_CHIPS).map((vocation) => (
                  <NextLink
                    key={vocation.id}
                    href={urls.DreamSearch({
                      industry,
                      zip,
                      shouldPromptForLocation,
                      filterQueryParams: { vocations: vocation.id },
                    })}
                    onClick={(e) => {
                      e.stopPropagation();
                      void analytics.track(
                        "Homepage - Industry Card Program Chip Click",
                        {
                          industry,
                          program: vocation.name,
                          zip,
                        }
                      );
                    }}
                  >
                    <ProgramChip variant="outlined" size="small">
                      {vocation.name}
                    </ProgramChip>
                  </NextLink>
                ))}
              </div>
            </IndustryCard>
          </CardWrapper>
        ))}
      </IndustryCards>
    </Section>
  );
};

const Section = styled("section")`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5rem 1rem 2rem;

  .title {
    margin-bottom: 3rem;
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding: 3rem 1rem 2rem;

    .title {
      margin-bottom: 2rem;
    }
  }
`;

const IndustryCards = styled("div")`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.25rem;

  a {
    text-decoration: none;
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  ${({ theme }) => theme.breakpoints.down("sm")} {
    grid-template-columns: 1fr;
    max-width: 100%;
    margin: 0 auto;
    gap: 1rem;
  }
`;

/** Extra top padding so the portrait has room to overflow above the card */
const CardWrapper = styled("div")`
  padding-top: 4rem;
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding-top: 3rem;
  }
`;

const IndustryCard = styled(Box)`
  background: white;
  border-radius: 1.25rem;
  border: 1px solid ${paletteOptions.dream.borderColor};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  display: flex;
  flex-direction: column;
  position: relative;
  flex: 1;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);

    .card_portrait {
      transform: translateX(-50%) scale(1.03);
    }
  }

  .card_main_link {
    display: flex;
    flex-direction: column;
  }

  /* Wrapper that clips sides & bottom but lets top overflow */
  .card_image_wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    overflow: visible;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }

  /*
   * Each card's image bg uses background-attachment: fixed so the motion blur
   * texture is positioned relative to the viewport. All four cards become
   * "windows" into the same continuous image.
   * On mobile, fixed attachment is unsupported (iOS), so fall back to scroll.
   */
  .card_image_bg {
    position: absolute;
    inset: 0;
    border-radius: 1.25rem 1.25rem 0 0;
    overflow: hidden;
    background: #d8e8f8 url("/motion-blur.webp") center center / cover fixed;

    @media (pointer: coarse) {
      background: #d8e8f8 url("/motion-blur.webp") center center / cover scroll;
    }
  }

  ${({ theme }) => theme.breakpoints.down("sm")} {
    .card_image_bg {
      background: #d8e8f8 url("/motion-blur.webp") center center / cover scroll;
    }
  }

  .card_image_gradient {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(
      to top,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 0.6) 40%,
      transparent 100%
    );
    pointer-events: none;
  }

  /* The portrait itself — anchored to bottom, overflows above the card */
  .card_portrait {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
    width: 85%;
    height: auto;
    object-fit: contain;
    object-position: bottom center;
    display: block;
    transition: transform 0.15s ease;
    pointer-events: none;
  }

  .card_info {
    padding: 0.75rem 1.25rem 0.75rem;
    position: relative;
    z-index: 2;
  }

  .card_label {
    font-size: 1.25rem;
    font-weight: 700;
    color: ${paletteOptions.text.primary};
    line-height: 1.2;
  }

  .card_count {
    color: ${paletteOptions.secondary.dark};
    font-size: 1rem;
    margin-top: 0.25rem;
  }

  .card_chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0 1.25rem 1.25rem;
    position: relative;
    z-index: 2;

    a {
      text-decoration: none;
    }
  }
`;

const ProgramChip = styled(Button)`
  &.MuiButton-root {
    border-radius: 2rem;
    min-height: 2.75rem;
    height: auto;
    font-size: 0.875rem;
    font-weight: 400;
    text-transform: none;
    color: ${paletteOptions.primary.main};
    border-color: ${paletteOptions.primary.main};
    padding: 0.375rem 1rem;
    min-width: auto;
    white-space: normal;
    text-align: left;
    line-height: 1.3;

    &:hover {
      background: rgba(4, 41, 158, 0.08);
      border-color: ${paletteOptions.primary.main};
    }

  }
`;
